<script setup lang="ts">
import AppTable from "@/components/app-table.vue";
import { useLoggedInUserFlags } from "@/composables/use-logged-in-user-flags";
import type { PhoneContractDTO } from "@/models/dtos/phone-contract-dto";
import { TableColumn } from "@/models/table-column";
import { RouteName } from "@/router";
import { contractsService } from "@/services/contracts-service";
import { authService } from "@/services/auth-service";
import { notificationsService } from "@/services/notifications-service";
import { computed, onBeforeMount, ref } from "vue";

const { userIsOneAdmin, userIsOneUser, userIsOneUserAdmin } =
  useLoggedInUserFlags();

const pageLoading = ref(false);
const loading = ref(false);

const columns = ref([
  TableColumn.for("contractName", "Kontrakt"),
  TableColumn.for("contractDescription", "Beskrivning"),
  TableColumn.for("phonenumber", "Telefonnummer"),
  TableColumn.for("create", "Skapa"),
  TableColumn.for("list", "Serviceärenden"),
]);

const phoneServiceContracts = ref<PhoneContractDTO[]>([]);

onBeforeMount(async () => {
  pageLoading.value = true;

  await loadContracts();

  pageLoading.value = false;
});

async function loadContracts() {
  try {
    const loggedInUser = authService.loggedInUser;

    const query: Record<string, unknown> = {};
    if (loggedInUser?.customerNumber) {
      query.customerNumber = loggedInUser?.customerNumber;
    }

    const isOneRole =
      userIsOneAdmin.value || userIsOneUser.value || userIsOneUserAdmin.value;
    if (!isOneRole && loggedInUser?.id) {
      query.includeUserId = loggedInUser?.id;
    }

    phoneServiceContracts.value = await contractsService.getPhoneContracts();
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid hämtning av telefon kontrakten.",
      type: "error",
      error,
    });
  }
}

const hasContracts = computed(() => phoneServiceContracts.value.length > 0);
</script>

<template>
  <section class="section px-0">
    <p class="title is-4">Telefontjänst-kontrakt</p>

    <app-loader v-if="pageLoading"></app-loader>

    <template v-if="!pageLoading && hasContracts">
      <div class="columns is-justify-content-space-between"></div>
    </template>
  </section>

  <section
    v-if="!pageLoading && hasContracts"
    class="section notification has-background-aero-light"
  >
    <app-table
      :columns="columns"
      :items="phoneServiceContracts"
      :loading="loading"
      export-csv
    >
      <template #column:create="{ item }">
        <router-link
          class="button"
          :to="{
            name: RouteName.ErrorReportsCreate,
            params: { contractId: item?.contractId },
          }"
          >Skapa</router-link
        >
      </template>
      <template #column:list="{ item }">
        <router-link
          class="button"
          :to="{
            name: RouteName.ServiceOrders,
            query: { contractId: item?.contractId },
          }"
          >Lista</router-link
        >
      </template>
    </app-table>
  </section>

  <section v-else-if="!pageLoading" class="section">
    <p class="subtitle is-5">
      De finns inga kontrakt som har telefontjänst kopplat till sig. Kontakta
      din lokala administratör för hjälp.
    </p>
  </section>
</template>
